import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

// The seven FAQ pairs from the Phase 2 handoff (Appendix A). These mirror the
// "Frequently asked questions" section rendered at the foot of the page — Google
// expects the marked-up answers to be visible on the page itself.
const FAQ_SCHEMA = {
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How many CE hours do Arizona real estate agents need?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '24 hours every two years for salespersons and associate brokers; 30 hours for designated, self-employed, and delegated associate brokers.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is there a limit to how many CE hours I can take per day in Arizona?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — 9 credit hours per day. A 24-hour package requires at least three days.'
      }
    },
    {
      '@type': 'Question',
      name: 'Are Water, Firewise, and Deed Fraud required?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, one hour each, for renewals on or after January 1, 2025.'
      }
    },
    {
      '@type': 'Question',
      name: 'What is the late fee for renewing an Arizona real estate license?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '$125, in addition to the regular renewal fee. Late renewal is available for up to one year after expiration.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I complete Arizona real estate CE online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. ADRE approves fully online, self-paced CE. No classroom time is required.'
      }
    },
    {
      '@type': 'Question',
      name: 'Who reports my CE to ADRE?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You do, through the Licensee Login portal. Schools do not submit on your behalf.'
      }
    },
    {
      '@type': 'Question',
      name: 'Do out-of-state CE courses count in Arizona?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Only if they carry ADRE approval and an ADRE course number. Most do not.'
      }
    }
  ]
};

// Set explicitly rather than taking ResourceLayout's shared default: this page's body
// was rewritten in September 2026 and the other guides were not, so the shared default
// would date-stamp pages that haven't actually been refreshed.
const UPDATED_LABEL = 'Updated September 2026 · Verified against azre.gov';

const RELATED = [
  { url: '/arizona-ce-2025-rule-changes', title: '2025 Rule Changes: Water, Firewise, Deed Fraud', desc: 'Exactly what changed January 1, 2025 and how to satisfy it.' },
  { url: '/how-fast-can-i-finish-arizona-ce', title: 'How Fast Can You Finish 24 Hours?', desc: 'The 9-hour daily cap and the fastest legal schedule.' },
  { url: '/arizona-ce-online', title: 'Can I Do All My CE Online?', desc: 'Yes. How ADRE-approved online CE actually works.' },
];

export default function CeRequirementsPage() {
  useSEO({
    title: 'Arizona Real Estate CE Requirements 2026 | Steady Education',
    description: 'Every ADRE CE requirement explained: the 24-hour breakdown by category, the 2025 Water, Firewise and Deed Fraud rules, deadlines and late fees.',
    url: canonicalUrl('/arizona-real-estate-ce-requirements'),
    jsonLd: guidePageSchema(FAQ_SCHEMA, {
      title: 'Arizona CE Requirements',
      url: canonicalUrl('/arizona-real-estate-ce-requirements'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="CE Requirements"
      updatedLabel={UPDATED_LABEL}
      title="Arizona Real Estate CE Requirements: The Complete 2026 Guide"
      subtitle="Arizona real estate licensees must complete 24 hours of ADRE-approved continuing education every two years to renew a salesperson or associate broker license, and 30 hours for designated and self-employed brokers. The hours aren't free-form: the Arizona Department of Real Estate requires specific categories, caps you at 9 hours per day, and it's the licensee — not the school — who reports the hours to ADRE."
      relatedGuides={RELATED}
    >
      <p>This guide covers all of it, updated for the rules that took effect January 1, 2025.</p>

      <h2>How many CE hours does Arizona require?</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>License type</th><th style={thRight}>Hours per 2-year cycle</th></tr>
          </thead>
          <tbody>
            <tr><td>Salesperson</td><td style={tdRight}>24</td></tr>
            <tr><td>Associate Broker</td><td style={tdRight}>24</td></tr>
            <tr><td>Designated Broker / Self-Employed Broker / Delegated Associate Broker</td><td style={tdRight}>30</td></tr>
          </tbody>
        </table>
      </div>
      <p>Your renewal cycle runs two years, ending on the last day of your renewal month. All hours must be completed within the 24 months before you renew — hours from a previous cycle don't carry forward.</p>

      <h2>What are the required CE categories for Arizona salespersons?</h2>
      <p>The 24 hours break down into mandatory categories and a small elective block:</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>Category</th><th style={thRight}>Hours</th></tr>
          </thead>
          <tbody>
            <tr><td>Agency Law</td><td style={tdRight}>3</td></tr>
            <tr><td>Commissioner's Standards</td><td style={tdRight}>3</td></tr>
            <tr><td>Contract Law</td><td style={tdRight}>3</td></tr>
            <tr><td>Disclosure</td><td style={tdRight}>3</td></tr>
            <tr><td>Fair Housing</td><td style={tdRight}>3</td></tr>
            <tr><td>Real Estate Legal Issues</td><td style={tdRight}>3</td></tr>
            <tr><td>Arizona Water</td><td style={tdRight}>1</td></tr>
            <tr><td>Firewise</td><td style={tdRight}>1</td></tr>
            <tr><td>Deed Fraud</td><td style={tdRight}>1</td></tr>
            <tr><td>General / Elective</td><td style={tdRight}>3</td></tr>
            <tr style={totalRow}><td>Total</td><td style={tdRight}>24</td></tr>
          </tbody>
        </table>
      </div>
      <p>The six 3-hour categories (18 hours) have been the core of Arizona CE for years. The three 1-hour topics — Water, Firewise, and Deed Fraud — were added for renewals on or after January 1, 2025. The remaining 3 hours can be any ADRE-approved course, including additional hours in a mandatory category.</p>

      <h2>What changed in 2025?</h2>
      <p>Beginning with renewals on or after January 1, 2025, ADRE requires one hour each of:</p>
      <ul>
        <li><strong>Arizona Water</strong> — water rights, assured water supply, and how water issues affect Arizona transactions.</li>
        <li><strong>Firewise</strong> — wildfire risk, defensible space, and disclosure considerations in fire-prone areas.</li>
        <li><strong>Deed Fraud</strong> — how title theft and forged-deed scams work, and how licensees can spot and prevent them.</li>
      </ul>
      <p>These three hours come out of what used to be a 6-hour general/elective block, so the total is still 24 — but if your package doesn't explicitly include Water, Firewise, and Deed Fraud, it won't satisfy the current requirement. Check before you buy. Our <Link to="/arizona-ce-2025-rule-changes">guide to the 2025 rule changes</Link> covers each topic in more detail.</p>

      <h2>What do brokers need?</h2>
      <p>Designated brokers, self-employed brokers, and delegated associate brokers complete 30 hours:</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr><th>Category</th><th style={thRight}>Hours</th></tr>
          </thead>
          <tbody>
            <tr><td>Broker Management Clinic 1</td><td style={tdRight}>3</td></tr>
            <tr><td>Broker Management Clinic 2</td><td style={tdRight}>3</td></tr>
            <tr><td>Broker Management Clinic 3 (3A or 3B)</td><td style={tdRight}>3</td></tr>
            <tr><td>Agency Law</td><td style={tdRight}>3</td></tr>
            <tr><td>Contract Law</td><td style={tdRight}>3</td></tr>
            <tr><td>Disclosure</td><td style={tdRight}>3</td></tr>
            <tr><td>Fair Housing</td><td style={tdRight}>3</td></tr>
            <tr><td>Real Estate Legal Issues</td><td style={tdRight}>3</td></tr>
            <tr><td>Arizona Water</td><td style={tdRight}>1</td></tr>
            <tr><td>Firewise</td><td style={tdRight}>1</td></tr>
            <tr><td>Deed Fraud</td><td style={tdRight}>1</td></tr>
            <tr><td>General / Elective</td><td style={tdRight}>3</td></tr>
            <tr style={totalRow}><td>Total</td><td style={tdRight}>30</td></tr>
          </tbody>
        </table>
      </div>
      <p>The three Broker Management Clinics replace the Commissioner's Standards requirement and add six hours. Associate brokers who are not designated or delegated follow the 24-hour salesperson schedule.</p>

      <h2>Can you complete all Arizona CE online?</h2>
      <p>Yes. ADRE approves online, self-paced courses, and there is no classroom requirement for renewal. All 24 hours — including the 2025 Water, Firewise, and Deed Fraud topics — can be completed online from any device.</p>
      <p>The one constraint is timing: ADRE caps CE at 9 credit hours per day. So a 24-hour package takes a minimum of three calendar days no matter how fast you work. Plan for at least three days before your renewal deadline — more if you want a buffer. <Link to="/arizona-ce-online">More on how online CE works.</Link></p>

      <h2>How fast can you finish 24 hours?</h2>
      <p>Because of the 9-hour daily cap, the fastest legal schedule is:</p>
      <ul>
        <li>Day 1: 9 hours</li>
        <li>Day 2: 9 hours</li>
        <li>Day 3: 6 hours</li>
      </ul>
      <p>Realistically, most licensees spread it over one to two weeks. Self-paced online courses let you stop and resume, so the total isn't tied to a session. <Link to="/how-fast-can-i-finish-arizona-ce">See the full timing breakdown.</Link></p>

      <h2>When is your renewal deadline?</h2>
      <p>Your license expires on the last day of the month, two years after it was issued or last renewed. The exact date is printed on your license and shown in your ADRE Licensee Login account. ADRE sends renewal reminders, but the responsibility to renew on time is yours. For the whole process in order, see <Link to="/arizona/how-to-renew">how to renew your Arizona real estate license, step by step</Link>.</p>
      <p>A practical timeline:</p>
      <ul>
        <li><strong>90 days out:</strong> buy your CE package, start the first courses.</li>
        <li><strong>30 days out:</strong> all hours complete, certificates in hand.</li>
        <li><strong>Before expiration:</strong> enter your CE in the ADRE portal and pay the renewal fee.</li>
      </ul>

      <h2>What happens if your license expires?</h2>
      <p>If you miss your renewal date, you can still renew late for up to one year after expiration by paying the renewal fee plus a $125 late fee. During that window you cannot practice real estate — no listings, no showings, no commissions — until the renewal is processed.</p>
      <p>After one year, the license cannot be renewed. You would have to re-qualify as a new applicant, including pre-licensing education and the state exam. <Link to="/arizona-license-expired-late-renewal">Full late-renewal guide.</Link></p>

      <h2>How do you submit CE hours to ADRE?</h2>
      <p>Your school does not report your hours. You do, through the ADRE Licensee Login portal:</p>
      <ol>
        <li>Log in to your Licensee Login account at azre.gov.</li>
        <li>Go to the Continuing Education section.</li>
        <li>Enter each course: school, course name, ADRE course number, category, hours, and completion date — all of which appear on your certificate.</li>
        <li>Submit and keep your certificates on file. ADRE audits licensees and can request proof.</li>
      </ol>
      <p>Enter courses as you complete them rather than all at once at the deadline — it's faster and easier to catch an error. <Link to="/how-to-submit-ce-to-adre">Step-by-step with screenshots.</Link></p>

      <h2>Which provider should you use?</h2>
      <p>Every ADRE-approved school satisfies the same state requirement, so the differences come down to format, focus, and whether the package already includes the 2025 topics. We put the options side by side so you can <Link to="/arizona-ce-provider-comparison">compare every major Arizona CE provider for 2026</Link>.</p>

      <h2>Does Arizona accept CE from other states?</h2>
      <p>Generally, no. Courses must be ADRE-approved and carry an ADRE course number. Out-of-state or national courses without Arizona approval do not count, even on the same topic. If you hold licenses in multiple states, plan on separate CE for each.</p>

      <h2>What about the REALTOR® Code of Ethics?</h2>
      <p>The National Association of REALTORS® ethics training is a NAR membership requirement, not an ADRE requirement. It does not substitute for any Arizona CE category. Some ADRE-approved courses cover ethics and may count toward Arizona hours, but check the ADRE category on the certificate.</p>

      <h2>Common mistakes that cost licensees time</h2>
      <ul>
        <li><strong>Buying a package that predates the 2025 rules.</strong> If it lacks Water, Firewise, and Deed Fraud, you're 3 hours short in the wrong categories.</li>
        <li><strong>Taking 24 hours in the wrong mix.</strong> Extra hours in one category don't cover a shortfall in another. Each mandatory category needs its own 3 hours.</li>
        <li><strong>Starting two days before the deadline.</strong> The 9-hour cap makes that impossible.</li>
        <li><strong>Assuming the school reported your hours.</strong> It didn't — you have to.</li>
        <li><strong>Repeating the same course.</strong> ADRE doesn't give credit for a course you already used in the same renewal cycle.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      {FAQ_SCHEMA.mainEntity.map(q => (
        <div key={q.name} style={{ marginBottom: 18 }}>
          <h3 style={faqQ}>{q.name}</h3>
          <p style={{ margin: 0 }}>{q.acceptedAnswer.text}</p>
        </div>
      ))}

      <div style={inlineCta}>
        <strong>Complete your Arizona CE with Steady Education.</strong> All 24 ADRE-required hours — including Water, Firewise, and Deed Fraud — in one self-paced package for $49.99, or $9.99 per course. Instant certificates on completion. ADRE School License S25-0002.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>See the Arizona CE package →</Link>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Source: Arizona Department of Real Estate (azre.gov). Always confirm your individual requirements through your ADRE Licensee Login.
      </p>
    </ResourceLayout>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 15, border: '1px solid #e8e2d6', borderRadius: 8, overflow: 'hidden' };
const thRight = { textAlign: 'right' };
const tdRight = { textAlign: 'right' };
const totalRow = { background: '#FAF7F2', fontWeight: 700 };
const faqQ = { fontSize: 16, fontWeight: 700, color: 'var(--forest-700)', margin: '0 0 6px' };
const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
